import {
  Container,
  Box,
  VStack,
  Image,
  Link,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  IconButton,
  useColorModeValue,
  Tooltip,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { MeContext } from '../../context/MeContext';
import { useGetMeQuery } from '../../store/redux/api/accountApi';
import { useDisclosure, useMediaQuery } from '@chakra-ui/react';
import CustomNavlink from '../../common/CustomNavlink';
import { CustomMenuOpenIcon } from '../../theme/icons/CustomMenuOpenIcon';

import logoDarkModeSrc from '../../assets/vectors/logoDarkMode.svg';
import logoSrc from '../../assets/images/logo-light.png';
import { CustomEventsMultiIcon } from '../../theme/multicolor-icons/CustomEventsMultiIcon';

import {
  CustomReactSelect,
  formatOptionLabelFilterEntity,
} from '../../components/common/CustomReactSelect';
import { useContext, useEffect, useState } from 'react';
import useOrganizationStatus from '../../hooks/useOrganisationStatus';
import { CustomCreditCardMultiIcon } from '../../theme/multicolor-icons/CustomCreditCardMultiIcon';
import { ROLES } from '../../constants/roles';
import useEntityManagement from '../../hooks/useEntitiesList';
import { CustomAnalyticsIcon } from '../../theme/icons/CustomAnalyticsIcon';
import { CustomScansIcon } from '../../theme/icons/CustomScansIcon';
import { CustomPassMultiIcon } from '../../theme/icons/CustomPassMultiIcon';
import CustomTemplateMultiIcon from '../../theme/icons/CustomTemplateIMultiIcon';
import CustomDownloadPageIcon from '../../theme/icons/CustomDownloadPageIcon';
import CustomReaderIcon from '../../theme/icons/CustomReaderIcon';
import CustomNotificationsIcon from '../../theme/icons/CustomNotificationsIcon';
import CustomGuidesIcon from '../../theme/icons/CustomGuidesIcon';
import CustomDeveloperToolsIcon from '../../theme/icons/CustomDeveloperToolsIcon';
import CustomSettingsIcon from '../../theme/icons/CustomSettingsIcon';
import { CustomLogoutMultiIcon } from '../../theme/multicolor-icons/CustomLogoutMultiIcon';

function Sidebar({ apiView, handleClick }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLargerThanLG] = useMediaQuery('(min-width: 62em)');
  const [isSmallerThanMD] = useMediaQuery('(max-width: 1280px)');
  const [isShortWindowHeight] = useMediaQuery('(max-height: 780px)');
  const modeLogoSrc = useColorModeValue(logoSrc, logoDarkModeSrc);
  const meCtx = useContext(MeContext);
  const {
    organisationStatus,
    trialExpiry,
    entities,
    filterEntity,
    eventsEnabled,
    account,
    billingEnabled,
    subscribed,
    cardlessTrialStartedAt,
  } = meCtx.state;

  const { data: me } = useGetMeQuery();
  const entitlementsActive = Boolean(me?.entitlements_active);

  const entitiesList = useEntityManagement(entities);
  const organisationId = account?.organisation_id;
  const { shouldRender } = useOrganizationStatus();
  const isSetUpConfirmed = meCtx?.isSetUpConfirmed() ?? true;
  const [selectedEntity, setSelectedEntity] = useState('');
  const [displayTooltip, setDisplayTooltip] = useState(false);
  const bgTooltip = useColorModeValue('orangePrimary');
  const textTooltip = useColorModeValue('#ffffff');
  let location = useLocation();
  const selectEntityLocation =
    location.pathname === '/pass-templates/create' ||
    location.pathname === '/events/create' ||
    location.pathname === '/readers/create' ||
    location.pathname === '/readers/reader-configuration' ||
    location.pathname === '/messaging' ||
    location.pathname === '/messaging/create';
  const isSubscriptionPage = location.pathname === '/plan-and-subscription';
  const openSidebarOnMobile =
    isSmallerThanMD &&
    entities.length > 1 &&
    selectEntityLocation &&
    !filterEntity;
  const handlePassesClick = () => {
    sessionStorage.removeItem('lastVisitedPageIndex');
    sessionStorage.removeItem('navigatedFromPassPage');
    sessionStorage.removeItem('filterValues');
    onClose();
  };

  // Calculate days remaining in trial
  let trialDaysRemaining = null;
  if (organisationStatus === 'trial' && !!trialExpiry) {
    const trialEnd = new Date(trialExpiry);
    const today = new Date();
    trialDaysRemaining = Math.ceil((trialEnd - today) / (1000 * 60 * 60 * 24));
  }

  const showSubscription =
    (billingEnabled === true && subscribed === true) ||
    cardlessTrialStartedAt ||
    entitlementsActive;

  const displayTrialEnd =
    isSetUpConfirmed && organisationStatus === 'trial' && trialDaysRemaining;

  useEffect(() => {
    if (openSidebarOnMobile) {
      onOpen();
      setTimeout(() => {
        setDisplayTooltip(true);
      }, 300);
    }
  }, [location]);

  useEffect(() => {
    if (openSidebarOnMobile && isOpen) {
      setDisplayTooltip(false);
      setTimeout(() => {
        setDisplayTooltip(true);
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectEntityLocation) {
      if (!filterEntity) {
        setTimeout(() => {
          setDisplayTooltip(true);
        }, 300);
      } else {
        setDisplayTooltip(false);
      }
    } else {
      setDisplayTooltip(false);
    }
  }, [location, filterEntity]);

  useEffect(() => {
    if (filterEntity && entitiesList?.length) {
      const match = entitiesList.find(
        (opt) => opt?.value?.id === filterEntity.id
      );
      setSelectedEntity(match ?? null);
    } else {
      setSelectedEntity(null);
    }
  }, [filterEntity, entitiesList]);

  const navStackProps = {
    className: 'sideNav',
    gap: isShortWindowHeight ? 2 : 1,
    mx: '-16px',
    w: 'calc(100% + 32px)',
  };

  const sidebarHeader = (
    <>
      <Link as={RouterLink} to="/">
        <Image
          w="160px"
          src={logoSrc}
          alt="PassEntry"
          loading="lazy"
          ml={3}
          my={6}
        />
      </Link>
      {entitiesList.length > 2 && shouldRender && (
        <Tooltip
          label={'Please select an entity'}
          hasArrow
          placement={!isSmallerThanMD ? 'right' : 'bottom'}
          fontSize="12px"
          bg={bgTooltip}
          color={textTooltip}
          w="205px"
          borderRadius="6px"
          textAlign="center"
          p="10px"
          isOpen={displayTooltip}
          left={-2}
        >
          <Box mt={2} mx={1} mb={5}>
            <CustomReactSelect
              options={entitiesList}
              formatOptionLabel={formatOptionLabelFilterEntity}
              onChange={(entity) => {
                setSelectedEntity(entity);
                meCtx.updateFilterEntity(entity.value);
              }}
              value={selectedEntity}
              placeholder="All entities"
              id="passTemplate"
              PassTemplate
            />
          </Box>
        </Tooltip>
      )}
    </>
  );

  const sidebarTopNavLinks = (
    <>
      <CustomNavlink
        onClick={onClose}
        to="/dashboard"
        title="Analytics"
        component={CustomAnalyticsIcon}
      />
      <CustomNavlink
        onClick={onClose}
        to="/pass-templates"
        title="Pass templates"
        component={CustomTemplateMultiIcon}
      />
      <CustomNavlink
        onClick={handlePassesClick}
        to="/passes"
        title="Passes"
        component={CustomPassMultiIcon}
      />
      <CustomNavlink
        onClick={onClose}
        to="/pass-download-pages"
        title="Download page"
        component={CustomDownloadPageIcon}
      />
      <CustomNavlink
        onClick={onClose}
        to="/messaging"
        title="Messaging"
        component={CustomNotificationsIcon}
      />
      {/* {eventsEnabled && (
        <CustomNavlink
          onClick={onClose}
          to="/events"
          title="Events"
          component={CustomEventsMultiIcon}
        />
      )} */}
      <CustomNavlink
        onClick={onClose}
        to="/readers"
        title="Readers"
        component={CustomReaderIcon}
      />
      <CustomNavlink
        onClick={onClose}
        to="/live-data"
        title="Scans"
        component={CustomScansIcon}
      />
    </>
  );

  const sidebarGuidesSection = (
    <VStack
      align="stretch"
      w="100%"
      spacing={isShortWindowHeight ? 2 : 1}
      py={3}
    >
      <CustomNavlink
        onClick={onClose}
        externalHref="https://docs.passentry.com/"
        title="Guides"
        subtitle="Learn how to create, issue & manage passes"
        component={CustomGuidesIcon}
        rightIcon
      />
      <CustomNavlink
        onClick={onClose}
        to="/developer-tools"
        title="Developer tools"
        component={CustomDeveloperToolsIcon}
      />
    </VStack>
  );

  const sidebarFooterNavLinks = (
    <>
      {showSubscription && (
        <CustomNavlink
          onClick={onClose}
          to="/my-account"
          title="Subscription"
          component={CustomCreditCardMultiIcon}
        />
      )}
      <CustomNavlink
        onClick={onClose}
        to="/account-settings"
        title="Settings"
        component={CustomSettingsIcon}
      />
      <CustomNavlink
        className="inactive"
        onClick={handleClick}
        title="Log out"
        component={CustomLogoutMultiIcon}
      />
    </>
  );

  const sidebarContent = (layout = 'drawer') => {
    if (layout === 'desktop') {
      return (
        <Container
          maxW="100%"
          w="full"
          minH="100%"
          flexShrink={0}
          display="grid"
          gridTemplateRows="auto auto minmax(0, 1fr) auto minmax(0, 1fr) auto"
        >
          <Box minW={0}>{sidebarHeader}</Box>
          <VStack {...navStackProps} align="stretch" minW={0} pt={5}>
            {sidebarTopNavLinks}
          </VStack>
          <Box minH={0} minW={0} aria-hidden />
          <Box className="sideNav" minW={0} mx="-16px" w="calc(100% + 32px)">
            {sidebarGuidesSection}
          </Box>
          <Box minH={0} minW={0} aria-hidden />
          <VStack {...navStackProps} align="stretch" minW={0} pb={5}>
            {sidebarFooterNavLinks}
          </VStack>
        </Container>
      );
    }

    return (
      <Container>
        {sidebarHeader}
        <VStack {...navStackProps} align="stretch" pt={5} pb={5}>
          {sidebarTopNavLinks}
          {sidebarGuidesSection}
          {sidebarFooterNavLinks}
        </VStack>
      </Container>
    );
  };

  return (
    <>
      {isLargerThanLG && !apiView ? (
        <Box
          bg="sidebarBg"
          h="100vh"
          w="250px"
          zIndex={1}
          pos="fixed"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Box
            flex="1 1 0"
            minH={0}
            display="flex"
            flexDirection="column"
            overflowY="auto"
            overflowX="hidden"
            sx={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255, 255, 255, 0.35) transparent',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.28)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(255, 255, 255, 0.45)',
              },
            }}
          >
            {sidebarContent('desktop')}
          </Box>
        </Box>
      ) : (
        isSmallerThanMD && (
          <>
            <IconButton
              variant="solid"
              height="40px"
              isRound
              bg="brand"
              color="white"
              className="primaryBoxShadow"
              aria-label="Open sidebar"
              icon={<CustomMenuOpenIcon boxSize="24px" />}
              onClick={onOpen}
              pos="fixed"
              top="10px"
              right="10px"
              zIndex={100}
              _focus={{ outline: 'none' }}
              _hover={{ bg: 'brandDark' }}
            />
            <Drawer
              isOpen={isOpen}
              placement="left"
              onClose={onClose}
              size="sm"
            >
              <DrawerOverlay />
              <DrawerContent bg="sidebarBg">
                <DrawerCloseButton color="white" />
                <DrawerBody
                  bg="sidebarBg"
                  p={0}
                  sx={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {
                      width: 0,
                      height: 0,
                    },
                    '&:hover': {
                      scrollbarWidth: 'thin',
                      scrollbarColor:
                        'rgba(255, 255, 255, 0.35) rgba(255, 255, 255, 0.06)',
                      msOverflowStyle: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.04)',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.28)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.45)',
                      },
                    },
                  }}
                >
                  {sidebarContent()}
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          </>
        )
      )}
    </>
  );
}

export default Sidebar;
